import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Heart, Users, DollarSign, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export const HowItWorksPage = () => {
  const steps = [
    {
      icon: Search,
      title: "1. Browse Amazing Deals",
      description: "Explore thousands of verified deals across all categories and emirates. Use filters to find exactly what you need - dining, shopping, travel, entertainment, and more.",
      features: [
        "Search by location, category, or keyword",
        "Filter by discount percentage or price range",
        "See deals ending soon or newly added",
        "All deals verified and updated daily"
      ]
    },
    {
      icon: Heart,
      title: "2. Save Your Favorites",
      description: "Found a deal you love? Save it to your personal collection. Get notified before it expires and never miss out on savings.",
      features: [
        "Create your wishlist of deals",
        "Organize by folders or categories",
        "Get expiry reminders",
        "Share deals with friends and family"
      ]
    },
    {
      icon: Users,
      title: "3. Redeem & Enjoy",
      description: "Click 'Get Deal' to see redemption instructions. Each deal has clear steps - whether it's a promo code, booking requirement, or voucher to show.",
      features: [
        "Clear redemption instructions",
        "Direct links to merchants",
        "Contact info if you need help",
        "Terms & conditions clearly stated"
      ]
    },
    {
      icon: DollarSign,
      title: "4. Track Your Savings",
      description: "Watch your savings grow! Your dashboard shows how much you've saved this month, year, and lifetime. See your impact on your budget.",
      features: [
        "Real-time savings calculator",
        "Monthly and yearly reports",
        "Savings goals and achievements",
        "Personal recommendations based on habits"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="how-it-works-heading">
              How Yalla ! Lets Save. Works
            </h1>
            <p className="text-xl text-emerald-50">
              Start saving money in 4 simple steps. It's free, fast, and easy!
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto space-y-16">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              const isEven = idx % 2 === 0;

              return (
                <div
                  key={idx}
                  className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
                      <Icon className="w-16 h-16 text-emerald-600" />
                    </div>
                  </div>

                  {/* Content */}
                  <Card className="flex-1">
                    <CardContent className="p-8">
                      <h2 className="text-2xl font-bold mb-4 text-emerald-600">
                        {step.title}
                      </h2>
                      <p className="text-lg text-gray-700 mb-6">
                        {step.description}
                      </p>
                      <ul className="space-y-3">
                        {step.features.map((feature, fIdx) => (
                          <li key={fIdx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Why Users Love Us</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">100%</div>
                  <div className="font-semibold mb-2">Free to Use</div>
                  <p className="text-sm text-gray-600">No hidden fees or subscriptions</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">1000+</div>
                  <div className="font-semibold mb-2">Active Deals</div>
                  <p className="text-sm text-gray-600">Updated daily across UAE</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">7</div>
                  <div className="font-semibold mb-2">Emirates Covered</div>
                  <p className="text-sm text-gray-600">From Dubai to Fujairah</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Saving?</h2>
          <p className="text-xl text-emerald-50 mb-8">
            Join thousands of smart savers across the UAE today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100">
                Browse Deals
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};